import { faPen, faPlus, faSearch, faTrash } from "@fortawesome/free-solid-svg-icons"
import { Modal, Pagination, Loading, Search } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import CountrySelector from "components/Shared/HeaderFilters/AssociatedCountriesSelector"
import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import { GET_ITEM_LIMIT, MARKET_MODULE, MFI_MODULE, regionList, RPME_MODULE, HH_MODULE } from "constants/constants"
import { formatDate } from "utils/helpers"
import useCommon from "hooks/useCommon"
import { UsersData } from "interfaces/dataInterfaces"
import { useEffect, useState } from "react"
import BaseTable, { AutoResizer, Column, ColumnShape } from "react-base-table"
import Select from "react-select"
import {
  useAssociatedCountriesQuery,
  useDeleteUserMutation,
  useGetModulesPermissionsQuery,
  useGetRolesQuery,
  useGetUserQuery,
} from "services/core/user"
import PermissionWrapper from "components/Shared/Common/PermissionWrapper"
import UserForm from "./UserForm"

const permissionsProps = {
  moduleId: [MARKET_MODULE, RPME_MODULE, MFI_MODULE, HH_MODULE],
  subModule: "users",
}
function UsersPage() {
  const { showSnackBar } = useCommon()

  const [pageNumber, setPageNumber] = useState(1)
  const [getItemLimit, setGetItemLimit] = useState(GET_ITEM_LIMIT)
  const [admCode, setAdmCode] = useState<undefined | number>(undefined)
  const [regionData, setRegionData] = useState<number | undefined>(undefined)
  const [query, setQuery] = useState<string | undefined>("")
  const [inputText, setInputText] = useState<string | undefined>(undefined)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [countriesData, setCountriesData] = useState<{ value: number; label: string }[]>([])

  const { data: countries } = useAssociatedCountriesQuery()
  const { data: roles } = useGetRolesQuery()

  const [currentItem, setCurrentItem] = useState<{
    item: UsersData & {
      mpRoleId?: string
      mfiRoleId?: string
      gorpRoleId?: string
      rpmeRoleId?: string
    }
    action: "edit" | "delete"
  } | null>(null)

  const { data: modulesPermissions, isFetching: isLoadingPermissions } = useGetModulesPermissionsQuery(
    { userID: (currentItem?.action === "edit" && currentItem?.item?.userId) || "" },
    { skip: !(currentItem?.action === "edit" && currentItem?.item?.userId) },
  )

  const { data: users, isFetching: isLoadingUsers } = useGetUserQuery({
    skip: pageNumber === 1 ? 0 : (pageNumber - 1) * getItemLimit,
    take: getItemLimit,
    adm0code: admCode,
    regionData,
    query,
  })
  const [deleteUser, { isLoading: isLoadingDelete }] = useDeleteUserMutation()

  const handleDelete = async (id: string) => {
    try {
      await deleteUser(id).unwrap()
      setCurrentItem(null)
      showSnackBar("User deleted successfully!")
    } catch (error) {
      showSnackBar("User not deleted", "error")
    }
  }

  const sortOrder = ["System Administrator", "Administrator", "Supervisor", "Contributor", "Visitor"]
  const rolesData: Array<{ value: string; label: string }> = []
  // eslint-disable-next-line array-callback-return
  roles?.map(c => {
    const sortIndex = sortOrder.findIndex(sI => c.name === sI)
    const data = { value: c.roleId, label: c.name }
    if (sortIndex !== -1) rolesData[sortIndex] = data
    else rolesData.push(data)
  })
  rolesData.push({ value: "", label: "No Access" })

  const columns: Array<ColumnShape<UsersData>> = [
    {
      key: "email",
      dataKey: "email",
      title: "Email",
      flexGrow: 0.5,
      width: 300,
    },
    {
      key: "firstName",
      dataKey: "firstName",
      title: "First Name",
      width: 150,
      maxWidth: 200,
    },
    {
      key: "lastName",
      dataKey: "lastName",
      title: "Last Name",
      width: 150,
      maxWidth: 200,
    },
    {
      key: "locationRole",
      dataKey: "locationRole",
      title: "Visibility",
      width: 100,
    },
    {
      key: "roleName",
      dataKey: "roleName",
      title: "Role",
      flexGrow: 1,
      width: 250,
    },
    {
      key: "lastAccessDate",
      title: "Last Access",
      width: 150,
      cellRenderer: ({ rowData }) => formatDate(rowData.lastAccessDate),
    },
    {
      key: "action",
      title: "",
      width: 250,
      align: Column.Alignment.CENTER,
      cellRenderer: ({ rowData }) => (
        <PermissionWrapper {...permissionsProps} feature="edit">
          <>
            <Button
              action={() => {
                setCurrentItem({ item: rowData, action: "edit" })
                setShowEditModal(true)
              }}
              label="Edit"
              className="btn-full blue action-btn"
              icon={faPen}
            />
            <Button
              action={() => setCurrentItem({ item: rowData, action: "delete" })}
              label="Delete"
              className="btn-full danger ml-1 action-btn"
              icon={faTrash}
            />
          </>
        </PermissionWrapper>
      ),
    },
  ]

  const getCountryData = async (event: any) => {
    setPageNumber(1)
    setAdmCode(event ? event.value : undefined)
  }

  const getRegionData = async (event: any) => {
    setPageNumber(1)
    setRegionData(event ? event.value : undefined)
  }

  const handleSearch = (e: any) => {
    setInputText(e)
  }

  const handleSubmitQuery = () => {
    setPageNumber(1)
    setQuery(inputText)
  }

  const handleSearchKey = (e: any) => {
    if (inputText && e?.code === "Enter") handleSubmitQuery()
  }

  useEffect(() => {
    if (!inputText && query) setQuery("")
  }, [inputText])

  useEffect(() => {
    if (!countries?.length) return
    const data = countries?.map(c => ({
      value: c.adm0Code,
      label: c.countryName,
    }))
    setCountriesData(data)
  }, [countries])

  useEffect(() => {
    if (modulesPermissions?.length && !isLoadingPermissions) {
      const permission: any = {}
      modulesPermissions?.forEach(mP => {
        switch (mP?.applicationModuleId) {
          case 1:
            permission.mpRoleId = mP?.roleId
            break
          case 2:
            permission.mfiRoleId = mP?.roleId
            break
          case 3:
            permission.gorpRoleId = mP?.roleId
            break
          case 4:
            permission.rpmeRoleId = mP?.roleId
            break
          case 5:
            permission.householdRoleId = mP?.roleId
            break
          case 6:
            permission.aimsRoleId = mP?.roleId
            break

          default:
            break
        }
      })
      const newItem = { ...currentItem?.item, ...permission }
      setCurrentItem({ action: "edit", item: newItem })
    }
  }, [showEditModal, modulesPermissions])

  return (
    <>
      {isLoadingDelete && <Loading />}
      <HeaderFilters>
        <div className="d-flex" style={{ width: "75%" }}>
          <CountrySelector onSelect={getCountryData} />
          <Select
            isSearchable
            className="dropdown mr-2"
            classNamePrefix="select"
            placeholder="Select region"
            options={regionList}
            onChange={value => getRegionData(value)}
            isClearable
          />
          <Search
            id="query"
            name="query"
            placeholder="Type here to filter results..."
            className="text-input mr-2"
            value={query}
            onChange={e => handleSearch(e)}
            onKeyUp={e => handleSearchKey(e)}
          />
          <Button action={handleSubmitQuery} label="Search" className="btn-full blue" icon={faSearch} />
        </div>
        <PermissionWrapper {...permissionsProps} feature="addNew">
          <Button
            action={() => {
              setCurrentItem(null)
              setShowAddModal(true)
            }}
            label="Add new user"
            className="btn-full green"
            icon={faPlus}
          />
        </PermissionWrapper>
      </HeaderFilters>
      <div
        className={isLoadingUsers ? "d-flex align-center justify-center" : ""}
        style={{ height: "100%", minHeight: "60vh", marginTop: "2%" }}
      >
        {isLoadingUsers ? (
          <Loading withOverlay={false} />
        ) : (
          <AutoResizer>
            {({ width, height }) => (
              <BaseTable
                data={users?.items}
                width={width}
                height={height}
                rowKey="userId"
                className="custom-table"
                rowHeight={38}
                headerHeight={45}
                columns={columns}
                emptyRenderer={() => <p>No records to display</p>}
              />
            )}
          </AutoResizer>
        )}
      </div>
      <Pagination
        totalItems={users?.count ?? 0}
        pageSize={getItemLimit}
        pageSizes={[10, 20, 30]}
        page={pageNumber}
        onChange={(event: any) => {
          setPageNumber(event.page)
          setGetItemLimit(event.pageSize)
        }}
      />
      <Modal
        open={currentItem?.action === "delete"}
        type="danger"
        modalHeading="Confirm Delete"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestSubmit={() => handleDelete(currentItem!.item.userId)}
        onRequestClose={() => setCurrentItem(null)}
      >
        Are you sure you want to delete the selected record?
      </Modal>

      {isLoadingPermissions ? (
        <Loading />
      ) : (
        showEditModal && (
          <UserForm
            item={currentItem?.item}
            show={showEditModal}
            closeModal={setShowEditModal}
            rolesData={rolesData}
            countriesData={countriesData}
            showSnackBar={showSnackBar}
          />
        )
      )}

      {showAddModal && (
        <UserForm
          show={showAddModal}
          closeModal={setShowAddModal}
          rolesData={rolesData}
          countriesData={countriesData}
          showSnackBar={showSnackBar}
        />
      )}
    </>
  )
}

export default UsersPage
